import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { MRT_Row } from "material-react-table";
import { useIntl } from "react-intl";
import { SkuQualification } from "services/KnowfullyAdminClient";
import { MULTIPLE_VALUES_INDICATOR } from "helpers/skuQualificationHelpers";
import SkuQualificationStatusChip from "../SkuQualificationStatusChip";
import ChildRow, { ColumnConfig } from "../ChildRow";
import { LegacyQualificationGroup } from "./grouping";

type LegacyExpandedChildTableProps = {
  readonly row: MRT_Row<LegacyQualificationGroup>;
  readonly onEdit: (qual: SkuQualification) => void;
  readonly onDelete: (qual: SkuQualification) => void;
  readonly isReadonly?: boolean;
};

const styles = {
  parentNotes: {
    fontSize: "0.875rem",
    color: "text.secondary",
  },
};

const formatDate = (date: Date | undefined | null) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString();
};

// State always varies across members; the rest only earns a column when the parent
// rolled it up to "Multiple Values".
const varies = (parentValue: unknown) => parentValue === MULTIPLE_VALUES_INDICATOR;

export default function LegacyExpandedChildTable({
  row,
  onEdit,
  onDelete,
  isReadonly,
}: LegacyExpandedChildTableProps) {
  const { formatMessage: t } = useIntl();
  const parent = row.original;

  if (!parent.isGroup || !parent.childQualifications) return null;

  const children = parent.childQualifications;

  if (children.length === 0) return null;

  const columns: (ColumnConfig<SkuQualification> & { show: boolean })[] = [
    {
      id: "verificationStatus",
      label: t({ id: "statusLabel" }),
      show: true,
      render: (qual) => <SkuQualificationStatusChip value={qual} />,
    },
    {
      id: "stateName",
      label: t({ id: "locationStateLabel" }),
      show: true,
      render: (qual) => qual.stateName,
    },
    {
      id: "courseNumber",
      label: t({ id: "courseNumber" }),
      show: varies(parent.courseNumber),
      render: (qual) => qual.courseNumber,
    },
    {
      id: "approvalCode",
      label: t({ id: "approvalCode" }),
      show: varies(parent.approvalCode),
      render: (qual) => qual.approvalCode,
    },
    {
      id: "approvalDate",
      label: t({ id: "approvalDate" }),
      show: varies(parent.approvalDate),
      render: (qual) => formatDate(qual.approvalDate),
    },
    {
      id: "releaseDate",
      label: t({ id: "skuFormReleaseDateLabel" }),
      show: varies(parent.releaseDate),
      render: (qual) => formatDate(qual.releaseDate),
    },
    {
      id: "creditSubmissionExpirationDate",
      label: t({ id: "skuFormCreditSubmissionExpirationDateLabel" }),
      show: varies(parent.creditSubmissionExpirationDate),
      render: (qual) => formatDate(qual.creditSubmissionExpirationDate),
    },
    {
      id: "credits",
      label: t({ id: "credits" }),
      show: varies(parent.credits),
      render: (qual) => qual.credits,
    },
    {
      id: "minimumCredit",
      label: t({ id: "minimumCredit" }),
      show: varies(parent.minimumCredit),
      render: (qual) => qual.minimumCredit,
    },
    {
      id: "partialCreditIncrement",
      label: t({ id: "partialCreditIncrement" }),
      show: varies(parent.partialCreditIncrement),
      render: (qual) => qual.partialCreditIncrement,
    },
  ];

  const visibleColumns = columns.filter((col) => col.show);
  const hasNotesVariation = varies(parent.notes);

  return (
    <Box sx={{ pl: 6, pr: 2, py: 2 }}>
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {hasNotesVariation && (
                <TableCell sx={{ width: 50, fontWeight: "bold" }} />
              )}
              {visibleColumns.map((col) => (
                <TableCell key={col.id} sx={{ fontWeight: "bold" }}>
                  {col.label}
                </TableCell>
              ))}
              {!isReadonly && (
                <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {children.map((child) => (
              <ChildRow
                key={child.id}
                child={child}
                visibleColumns={visibleColumns}
                hasNotes={hasNotesVariation}
                onEdit={isReadonly ? undefined : onEdit}
                onDelete={isReadonly ? undefined : onDelete}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {parent.notes && parent.notes !== MULTIPLE_VALUES_INDICATOR && (
        <Paper sx={{ mt: 2, p: 2 }} elevation={1}>
          <Box sx={styles.parentNotes}>
            <strong>Notes:</strong> {parent.notes}
          </Box>
        </Paper>
      )}
    </Box>
  );
}
